import React, { useEffect, Fragment, useState } from "react";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { showToast } from "../../../components/ToastifyNotification";
import Breadcrumb from "../../../components/common/breadcrumb";

// Excel + PDF
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { DischargeReportApi } from "../../../api/ipd/ipdReportsAPI";

const DischargeReport = () => {

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [search, setSearch] = useState("");

    const dispatch = useDispatch();

    // Fetch Data
    const fetchData = async () => {
        dispatch({ type: "LOADER_START" });

        try {
            const req = {
                fromDate,
                toDate,
                search
            };

            const response = await DischargeReportApi(req);

            if (response.success) {
                showToast("success", response.message);

                const formatted = response.data.map((item, index) => ({
                    index: index + 1,
                    id: item._id,
                    billNo: item?.billNo || "",
                    ipdNo: item?.ipdEntryId?.ipdNo || "",
                    dischargeDate: item?.dischargeDate?.split("T")[0] || "",
                    dischargeTime: item?.dischargeTime || "",
                    noOfDays: item?.noOfDays || "",
                    roomNo: item?.roomDetails?.roomNo || "",
                    patientName:
                        (item?.opdEntryId?.patientNamePrefix
                            ? item?.opdEntryId?.patientNamePrefix + " "
                            : "") +
                        (item?.opdEntryId?.patientName || item?.ipdEntryId?.patientName || ""),
                    sex: item?.opdEntryId?.sex || item?.ipdEntryId?.sex || "",
                    age: item?.opdEntryId?.age || item?.ipdEntryId?.age || "",
                    mobileNo: item?.opdEntryId?.mobileNo || item?.ipdEntryId?.mobileNo || "",
                    address: item?.opdEntryId?.address || item?.ipdEntryId?.address || "",
                    patientStatus: item.patientStatus || "",
                    totalCharges: item.totalCharges || "",
                    netAmount: item.netAmount || "",
                    icdx: item.icdx || "",
                    advance: item.advance || "",
                    discountPercentage: item.discountPercentage || "",
                    discountAmount: item.discountAmount || "",
                    discountBy: item.discountBy || "",
                    paymentMode: item.paymentMode || "",
                    toPay: item.toPay || "",
                    finalDiagnosis: item.finalDiagnosis || "",
                    remark: item.remark || "",
                    createdBy: item.createdBy?.name || "",
                    createdAt: item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString("en-IN")
                        : "",
                    updatedBy: item.updatedBy?.name || "",
                    updatedAt: item.updatedAt
                        ? new Date(item.updatedAt).toLocaleDateString("en-IN")
                        : "",
                }));

                setData(formatted);
                setFilteredData(formatted);

            } else {
                showToast("error", response.message);
            }

        } catch (err) {
            showToast("error", err.message);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    // Search Action
    const handleSearch = () => {
        fetchData();
    };

    // Headers
    const headers = [
        { label: "S.No", key: "index" },
        { label: "Bill No", key: "billNo" },
        { label: "IPD No", key: "ipdNo" },
        { label: "Discharge Date", key: "dischargeDate" },
        { label: "Discharge Time", key: "dischargeTime" },
        { label: "No. of Days", key: "noOfDays" },
        { label: "Room No", key: "roomNo" },
        { label: "Patient Name", key: "patientName" },
        { label: "Sex", key: "sex" },
        { label: "Age", key: "age" },
        { label: "Mobile No", key: "mobileNo" },
        { label: "Address", key: "address" },
        { label: "Patient Status", key: "patientStatus" },
        { label: "Total Charge", key: "totalCharges" },
        { label: "Net Amount", key: "netAmount" },
        { label: "ICDX", key: "icdx" },
        { label: "Advance", key: "advance" },
        { label: "Discount %", key: "discountPercentage" },
        { label: "Discount Amount", key: "discountAmount" },
        { label: "Discount By", key: "discountBy" },
        { label: "Payment Mode", key: "paymentMode" },
        { label: "To Pay", key: "toPay" },
        { label: "Final Diagnosis", key: "finalDiagnosis" },
        { label: "Remark", key: "remark" },
        { label: "Created By", key: "createdBy" },
        { label: "Created At", key: "createdAt" },
        { label: "Updated By", key: "updatedBy" },
        { label: "Updated At", key: "updatedAt" },
    ];

    // Table Columns
    const columns = headers.map((h) => ({
        name: h.label,
        selector: (row) => row[h.key],
        sortable: true,
        width: "170px",
    }));

    // Export Excel
    const exportToExcel = () => {
        const sheetData = filteredData.map((row) => {
            let obj = {};
            headers.forEach((h) => (obj[h.label] = row[h.key]));
            return obj;
        });

        const worksheet = XLSX.utils.json_to_sheet(sheetData);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Discharge Report");
        XLSX.writeFile(workbook, "Discharge_Report.xlsx");
    };

    // Export PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Discharge Report", 14, 10);

        const tableColumn = headers.map((h) => h.label);
        const tableRows = filteredData.map((row) => headers.map((h) => row[h.key]));

        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });

        doc.save("Discharge_Report.pdf");
    };

    // Reset Filters
    const resetFilters = () => {
        setFromDate("");
        setToDate("");
        setSearch("");
        setFilteredData([]);
    };

    return (
        <Fragment>
            <Breadcrumb parent="IPD" title="Discharge Report" />

            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header">
                                <h5>Discharge Report</h5>

                                {/* FILTERS SECTION */}
                                <div className="row mt-3">

                                    <div className="col-sm-2">
                                        <label>From Date</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm-2">
                                        <label>To Date</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm-3">
                                        <label>Search</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            value={search}
                                            placeholder="Search..."
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm text-end">
                                        <button
                                            className="btn btn-primary mt-4 me-2"
                                            onClick={handleSearch}
                                        >
                                            Search
                                        </button>

                                        <button
                                            className="btn btn-warning mt-4"
                                            onClick={resetFilters}
                                        >
                                            Reset
                                        </button>
                                    </div>

                                    <div className="col-sm text-end mt-4">
                                        <button
                                            className="btn btn-success me-2"
                                            onClick={exportToExcel}
                                        >
                                            Excel
                                        </button>

                                        <button
                                            className="btn btn-danger"
                                            onClick={exportToPDF}
                                        >
                                            PDF
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* TABLE SECTION */}
                            <div className="card-body">
                                <DataTable
                                    columns={columns}
                                    data={filteredData}
                                    pagination
                                    highlightOnHover
                                    dense
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default DischargeReport;
